import { getConfigurationValue } from './getConfigurationValue'
import type {
  AppConfiguration,
  ConfigurationCollectionPath,
  ConfigurationCollectionValue,
  ConfigurationPath,
  FieldValue,
} from './types'
import { useConfigurationContext } from './useConfigurationContext'

/**
 * Type for validating and extracting hook paths from configuration.
 * Similar to UseFormRegister, but for configuration paths.
 * Uses the same FieldPath logic from core types.
 */
type UseConfigurationRegister<TConfig extends object> = <
  TPath extends ConfigurationPath<TConfig> & string =
    ConfigurationPath<TConfig> & string,
>(
  path: TPath,
) => FieldValue<TConfig, TPath>

/**
 * Type for validating and extracting collection paths from configuration.
 */
type UseConfigurationCollectionRegister<TConfig extends object> = <
  TPath extends ConfigurationCollectionPath<TConfig> & string =
    ConfigurationCollectionPath<TConfig> & string,
>(
  path: TPath,
) => ConfigurationCollectionValue<TConfig, TPath>

export const createLenteHooks = <C extends AppConfiguration>() => {
  /**
   * Hook for accessing values from the hooks configuration.
   * Provides type-safe access with validated key paths.
   *
   * @param key - The path to the hook in the configuration (e.g., 'logger' or 'nested.hook')
   * @returns The hook value at the specified path
   *
   * @example
   * const logger = useHook('logger')
   * const nestedHook = useHook('nested.hook')
   */
  const useHook: UseConfigurationRegister<C['hooks']> = <
    K extends ConfigurationPath<C['hooks']>,
  >(
    key: K,
  ): FieldValue<C['hooks'], K> => {
    const context = useConfigurationContext<C>()

    return getConfigurationValue(context.hooks, key)
  }

  /**
   * Hook for accessing values from the services configuration.
   * Provides type-safe access with validated key paths.
   *
   * @param key - The path to the service in the configuration (e.g., 'deleteCommitment' or 'api.service')
   * @returns The service value at the specified path
   *
   * @example
   * const deleteService = useService('deleteCommitment')
   * const apiService = useService('api.service')
   */
  const useService: UseConfigurationRegister<C['services']> = <
    K extends ConfigurationPath<C['services']>,
  >(
    key: K,
  ): FieldValue<C['services'], K> => {
    const context = useConfigurationContext<C>()

    return getConfigurationValue(context.services, key)
  }

  /**
   * Hook for accessing values from the components configuration.
   * Provides type-safe access with validated key paths.
   *
   * @param key - The path to the component in the configuration (e.g., 'flag' or 'ui.button')
   * @returns The component value at the specified path
   *
   * @example
   * const Flag = useComponent('flag')
   * const Button = useComponent('ui.button')
   */
  const useComponent: UseConfigurationRegister<C['components']> = <
    K extends ConfigurationPath<C['components']>,
  >(
    key: K,
  ): FieldValue<C['components'], K> => {
    const context = useConfigurationContext<C>()

    return getConfigurationValue(context.components, key)
  }

  /**
   * Hook for accessing collections from the components configuration.
   * Returns an array of entries [key, value] where the value is non-empty.
   *
   * @param key - The path to the collection in the configuration (must start with 'collection')
   * @returns An array of [key, value] tuples for non-empty values
   *
   * @example
   * const items = useComponentCollection('collectionItems')
   * // Returns: [['item1', Component1], ['item2', Component2]]
   */
  const useComponentCollection: UseConfigurationCollectionRegister<
    C['components']
  > = <K extends ConfigurationCollectionPath<C['components']>>(
    key: K,
  ): ConfigurationCollectionValue<C['components'], K> => {
    const context = useConfigurationContext<C>()

    return Object.entries(
      getConfigurationValue(context.components, key) as Record<string, unknown>,
    ).filter(([, v]) => v) as ConfigurationCollectionValue<C['components'], K>
  }

  return { useHook, useService, useComponent, useComponentCollection }
}
